import { Card, useDialog } from "@fpsoftware/mvp";
import type { ComponentProps } from "react";
import { TbEdit } from "react-icons/tb";
import { DEBT_TYPE_LABELS, formatMoney, formatPercent } from "shared";
import { Button } from "@/components/Button";
import { EditMortgageDebtsDialog } from "@/mortgages/EditMortgageDebtsDialog";
import { useMortgage } from "@/mortgages/hooks";

export function MortgageDebtsCard(
  props: { mortgageID: number } & ComponentProps<typeof Card>,
) {
  const { mortgageID, ...rest } = props;
  const mortgage = useMortgage(mortgageID);
  const { open } = useDialog();

  const { debts, blendedRate } = mortgage.remortgage;

  const handleEdit = () => {
    open(EditMortgageDebtsDialog, { mortgageID: mortgage.ID });
  };

  return (
    <Card {...rest}>
      <Card.Header>
        Other loans
        <Button icon={TbEdit} size="sm" onClick={handleEdit}>
          Edit
        </Button>
      </Card.Header>
      {debts.length === 0 ? (
        <Card.Body>
          <i>No other loans added.</i>
        </Card.Body>
      ) : (
        <>
          <Card.List>
            {debts.map((debt) => (
              <Card.ListItem
                key={debt.id}
                label={`${DEBT_TYPE_LABELS[debt.type]} — ${debt.name}`}
              >
                {formatMoney(debt.balance)}
                <br />
                <small>
                  {formatPercent(debt.interestRate)}
                  {` · ${formatMoney(debt.monthlyPayment)}/mo`}
                  {debt.monthsLeft !== null ? (
                    ` · ${debt.monthsLeft} payments left`
                  ) : (
                    <span style={{ color: "var(--color-error)" }}>
                      {" · payment too low to ever pay off"}
                    </span>
                  )}
                </small>
              </Card.ListItem>
            ))}
          </Card.List>
          <Card.Footer>
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Blended rate</span>
              <strong>{formatPercent(blendedRate)}</strong>
            </span>
          </Card.Footer>
        </>
      )}
    </Card>
  );
}
